import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, ExternalLink, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { SettingCell } from '@/components/setting-cell'
import { StatusPill } from '@/components/status-pill'
import {
  ModelUnavailableError,
  useProductModel3D,
  useProductModel3DFile,
  type ProductModel3D,
} from '@/api/product-models'
import {
  catalogSize,
  formatSize,
  modelProblem,
  type Size,
} from '@/components/products/product-model-3d-text'

const INCHES_PER_METRE = 39.3701

type Dimensions = { x: number; y: number; z: number }
type ModelViewerElement = HTMLElement & { getDimensions?: () => Dimensions }

declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'model-viewer': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        src?: string
        alt?: string
        'camera-controls'?: string
        'touch-action'?: string
        'interaction-prompt'?: string
        'shadow-intensity'?: string
        exposure?: string
        'environment-image'?: string
      }
    }
  }
}

const VERDICTS: Record<string, string> = {
  good: 'Good',
  acceptable: 'Acceptable',
  incomplete: 'Incomplete',
  broken: 'Broken',
  wrong_product: 'Wrong product',
}

const FILE_KINDS: Record<string, string> = {
  AutoCad3D: 'AutoCAD drawing',
  Revit: 'Revit family',
}

function storeState(record: ProductModel3D | null | undefined, failed: boolean) {
  if (record?.status === 'published') return <StatusPill tone="success">On the store</StatusPill>
  if (failed || record?.status === 'failed') return <StatusPill tone="warning">No model</StatusPill>
  return <StatusPill tone="neutral">Not on the store</StatusPill>
}

/** The interactive viewer: drag to turn, scroll or pinch to zoom. Reports the model's size once drawn. */
function ModelViewer({
  file,
  alt,
  onSize,
}: {
  file: Blob
  alt: string
  onSize: (size: Size) => void
}) {
  const ref = useRef<ModelViewerElement>(null)
  const [url, setUrl] = useState<string | null>(null)
  const [drawn, setDrawn] = useState(false)

  useEffect(() => {
    void import('@google/model-viewer')
  }, [])

  useEffect(() => {
    const next = URL.createObjectURL(file)
    setUrl(next)
    return () => URL.revokeObjectURL(next)
  }, [file])

  useEffect(() => {
    const element = ref.current
    if (!element) return
    const loaded = () => {
      setDrawn(true)
      const dimensions = element.getDimensions?.()
      if (dimensions) {
        onSize({
          width: dimensions.x * INCHES_PER_METRE,
          depth: dimensions.z * INCHES_PER_METRE,
          height: dimensions.y * INCHES_PER_METRE,
        })
      }
    }
    element.addEventListener('load', loaded)
    return () => element.removeEventListener('load', loaded)
  }, [onSize, url])

  return (
    <div className="relative h-[clamp(300px,55vh,520px)] bg-muted/40">
      {url && (
        <model-viewer
          ref={ref}
          src={url}
          alt={alt}
          camera-controls=""
          touch-action="pan-y"
          interaction-prompt="none"
          shadow-intensity="1"
          exposure="0.85"
          environment-image="neutral"
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: 'transparent',
          }}
        >
          {/* Our own spinner shows while it loads. */}
          <div slot="progress-bar" />
        </model-viewer>
      )}
      {!drawn && <ViewerLoading />}
    </div>
  )
}

function ViewerLoading() {
  return (
    <div className="absolute inset-0 flex items-center justify-center" role="status">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden />
      <span className="sr-only">Loading the 3D model</span>
    </div>
  )
}

function Problem({
  reason,
  detail,
  problems,
}: {
  reason: string
  detail: string
  problems?: string | null
}) {
  const problem = modelProblem(reason, detail, problems)
  return (
    <div className="flex gap-3 px-4 py-4">
      <AlertTriangle
        className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-500"
        aria-hidden
      />
      <div className="min-w-0 space-y-1 text-sm">
        <p>{problem.text}</p>
        {problem.sizes && (
          <p className="text-muted-foreground tabular-nums">
            Model {problem.sizes.model} · AutoQuotes {problem.sizes.catalog}
          </p>
        )}
      </div>
    </div>
  )
}

/** The product's store 3D model (USA-1624): the model itself, or why it has none. */
export function ProductModel3DPanel({
  productId,
  model,
  width,
  depth,
  height,
}: {
  productId: string
  model: string
  width?: string | number | null
  depth?: string | number | null
  height?: string | number | null
}) {
  const status = useProductModel3D(productId)
  const record = status.data?.record
  const drawing = status.data?.drawing
  const recordedFailure = record?.status === 'failed'
  const [showRejected, setShowRejected] = useState(false)
  const wantFile = !!drawing && (!recordedFailure || showRejected)
  const file = useProductModel3DFile(productId, wantFile)
  const [size, setSize] = useState<Size | null>(null)
  const catalog = catalogSize(width, depth, height)

  const unavailable = file.error instanceof ModelUnavailableError ? file.error : null
  const failed = recordedFailure || !!unavailable
  // The viewer and its facts sit side by side; a product without a model shows why, facts below.
  const showsModel = wantFile && !file.isError

  return (
    <section
      aria-labelledby="product-model-3d"
      className="overflow-hidden rounded-xl border bg-card"
    >
      <div className="flex items-center gap-2 border-b px-4 py-3">
        <h2 id="product-model-3d" className="text-sm font-semibold">
          3D model
        </h2>
        {status.data && drawing && storeState(record, failed)}
      </div>

      {status.isLoading ? (
        <Skeleton className="m-4 h-64" />
      ) : status.isError ? (
        <p className="px-4 py-4 text-sm text-destructive">Couldn't load the 3D model.</p>
      ) : !drawing ? (
        <p className="px-4 py-4 text-sm text-muted-foreground">
          AutoQuotes has no 3D file for this product.
        </p>
      ) : (
        <div className={cn(showsModel && 'lg:grid lg:grid-cols-[minmax(0,1fr)_17rem]')}>
          <div className={cn('min-w-0 border-b', showsModel && 'lg:border-b-0 lg:border-r')}>
            {recordedFailure && record && (
              <div className="flex flex-wrap items-start justify-between gap-x-4">
                <Problem
                  reason={record.reason ?? ''}
                  detail={record.detail ?? ''}
                  problems={record.review?.problems}
                />
                {record.reason === 'review_rejected' && !showRejected && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mx-4 mb-4 sm:mt-3"
                    onClick={() => setShowRejected(true)}
                  >
                    Show model
                  </Button>
                )}
              </div>
            )}
            {unavailable && <Problem reason={unavailable.reason} detail={unavailable.detail} />}
            {file.isError && !unavailable && (
              <div className="flex items-center gap-3 px-4 py-4 text-sm">
                <span className="text-destructive">Couldn't load the 3D model.</span>
                <Button variant="outline" size="sm" onClick={() => void file.refetch()}>
                  Try again
                </Button>
              </div>
            )}
            {showsModel && file.isLoading && (
              <div className="relative h-[clamp(300px,55vh,520px)] bg-muted/40">
                <ViewerLoading />
              </div>
            )}
            {file.data && (
              <div className={recordedFailure ? 'border-t' : undefined}>
                <ModelViewer file={file.data} alt={`3D model of ${model}`} onSize={setSize} />
              </div>
            )}
          </div>
          <div
            className={cn(
              '-mb-px -mr-px grid grid-cols-2',
              showsModel ? 'lg:mr-0 lg:grid-cols-1 lg:content-start' : 'sm:grid-cols-4',
            )}
          >
            {showsModel && (
              <SettingCell
                label="Size"
                className={cn(showsModel && 'lg:border-r-0')}
                below={
                  catalog && (
                    <p className="text-xs text-muted-foreground">
                      AutoQuotes{' '}
                      <span className="whitespace-nowrap tabular-nums">{formatSize(catalog)}</span>
                    </p>
                  )
                }
              >
                <span className="text-sm font-medium tabular-nums">
                  {size ? formatSize(size) : '-'}
                </span>
              </SettingCell>
            )}
            {(record?.review || showsModel) && (
              <SettingCell
                label="AI review"
                className={cn(showsModel && 'lg:border-r-0')}
                below={
                  record?.review?.problems && !recordedFailure ? (
                    <p className="text-xs text-muted-foreground">{record.review.problems}</p>
                  ) : null
                }
              >
                <span className="text-sm font-medium">
                  {record?.review
                    ? (VERDICTS[record.review.match] ?? record.review.match)
                    : 'Not reviewed'}
                </span>
              </SettingCell>
            )}
            <SettingCell
              label="From"
              className={cn(showsModel && 'col-span-2 lg:col-span-1 lg:border-r-0')}
            >
              {drawing.store_url ? (
                // eslint-disable-next-line no-restricted-syntax -- external URL
                <a
                  href={drawing.store_url}
                  className="inline-flex min-w-0 items-center gap-1 text-sm text-primary hover:underline"
                >
                  <span className="truncate">{FILE_KINDS[drawing.media_type] ?? drawing.name}</span>
                  <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
                </a>
              ) : (
                <span className="text-sm">{FILE_KINDS[drawing.media_type] ?? drawing.name}</span>
              )}
            </SettingCell>
          </div>
        </div>
      )}
    </section>
  )
}
